import { LocalDateTime, LocalTime } from '@js-joda/core';
import _ from 'lodash';

export interface ClosedOpenRange<T> {
  low: T;
  high: T;
}

export function range<T>(low: T, high: T): ClosedOpenRange<T> {
  return { low, high };
}

/**
 * Represents a set of ranges of any type T that supports <.
 *
 * Note: This uses an O(n) algorithm. O(log n) should be possible using a
 * binary search tree based approach based on an interval tree or similar,
 * but I didn't feel like it was necessary since we don't expect this to get
 * used with more than about 5 ranges total. If this becomes a performance
 * bottleneck then maybe we should revisit that decision.
 */
export default class RangeSet<T> {
  // Invariant: Ranges in this array never overlap and are sorted in ascending
  // order.
  private entries: Array<ClosedOpenRange<T>>;

  /**
   * Creates a new, empty range set.
   */
  public static empty<T>(): RangeSet<T> {
    return new RangeSet<T>([]);
  }

  /**
   * Constructs a new range set with the given entries, which must satisfy the
   * invariants. For private use only.
   */
  private constructor(entries: Array<ClosedOpenRange<T>>) {
    this.entries = entries;
  }

  /**
   * Functionally adds the given range to the set, returning a new range.
   */
  public addRange(newRange: ClosedOpenRange<T>): RangeSet<T> {
    // Algorithm: We can divide this.entries into three categories:
    //   1. All ranges that are completely below the input range;
    //   2. All ranges that touch the input range, and
    //   3. All ranges that are completely above the input range.
    // Ranges that are completely below or above the interval will remain so in
    // the output. Ranges that touch the input range will all get merged into
    // one joined range that spans from the bottom of the smallest to the top of
    // the largest.
    //
    const below: Array<ClosedOpenRange<T>> = [];
    const above: Array<ClosedOpenRange<T>> = [];
    let intersectingLowerBound: T = newRange.low;
    let intersectingUpperBound: T = newRange.high;
    _.forEach(this.entries, (range) => {
      switch (compareRange(range, newRange)) {
        case 'BELOW':
          below.push(range);
          break;
        case 'ABOVE':
          above.push(range);
          break;
        case 'TOUCHING':
          intersectingLowerBound = lesser(intersectingLowerBound, range.low);
          intersectingUpperBound = greater(intersectingUpperBound, range.high);
          break;
      }
    });
    const intersectingRange = {
      low: intersectingLowerBound,
      high: intersectingUpperBound,
    };
    return new RangeSet<T>(_.concat(below, [intersectingRange], above));
  }

  /**
   * Functionally removes all values the given range from the set, returning a
   * new range.
   */
  public removeRange(rangeToRemove: ClosedOpenRange<T>): RangeSet<T> {
    const newRanges: Array<ClosedOpenRange<T>> = [];
    _.forEach(this.entries, (range) => {
      const isLocalDateTime = checkIsRangeLocalDateTime(range, rangeToRemove);
      const isLocalTime = checkIsRangeLocalTime(range, rangeToRemove);
      switch (compareRange(range, rangeToRemove)) {
        case 'BELOW':
        case 'ABOVE':
          newRanges.push(range);
          break;
        case 'TOUCHING':
          if (isLocalDateTime) {
            const modifiedRange = range as ClosedOpenRange<LocalDateTime>;
            const modifiedRangeToRemove = rangeToRemove as ClosedOpenRange<LocalDateTime>;
            if (modifiedRange.low.isBefore(modifiedRangeToRemove.low)) {
              newRanges.push({
                low: range.low,
                high: rangeToRemove.low,
              });
            }
            if (modifiedRangeToRemove.high.isBefore(modifiedRange.high)) {
              newRanges.push({
                low: rangeToRemove.high,
                high: range.high,
              });
            }
          } else if (isLocalTime) {
            const modifiedRange = range as ClosedOpenRange<LocalTime>;
            const modifiedRangeToRemove = rangeToRemove as ClosedOpenRange<LocalTime>;
            if (modifiedRange.low.isBefore(modifiedRangeToRemove.low)) {
              newRanges.push({
                low: range.low,
                high: rangeToRemove.low,
              });
            }
            if (modifiedRangeToRemove.high.isBefore(modifiedRange.high)) {
              newRanges.push({
                low: rangeToRemove.high,
                high: range.high,
              });
            }
          } else {
            if (range.low < rangeToRemove.low) {
              newRanges.push({
                low: range.low,
                high: rangeToRemove.low,
              });
            }
            if (rangeToRemove.high < range.high) {
              newRanges.push({
                low: rangeToRemove.high,
                high: range.high,
              });
            }
          }
      }
    });
    return new RangeSet<T>(newRanges);
  }

  /**
   * Functionally removes all parts of the range set that fall outside the
   * bounds of the given range.
   */
  public intersect(intersectRange: ClosedOpenRange<T>): RangeSet<T> {
    const newRanges: Array<ClosedOpenRange<T>> = [];
    _.forEach(this.entries, (range) => {
      switch (compareRange(range, intersectRange)) {
        case 'BELOW':
        case 'ABOVE':
          return;
        case 'TOUCHING':
          newRanges.push({
            low: greater(range.low, intersectRange.low),
            high: lesser(range.high, intersectRange.high),
          });
      }
    });
    return new RangeSet<T>(newRanges);
  }

  /**
   * Functionally removes all parts of the range set that fall outside the
   * bounds of the given range set.
   */
  public intersectRangeSet(intersectRangeSet: RangeSet<T>): RangeSet<T> {
    const newRanges: Array<ClosedOpenRange<T>> = [];
    _.forEach(this.entries, (range) => {
      _.forEach(intersectRangeSet.entries, (intersectRange) => {
        switch (compareRange(range, intersectRange)) {
          case 'BELOW':
          case 'ABOVE':
            return;
          case 'TOUCHING':
            newRanges.push({
              low: greater(range.low, intersectRange.low),
              high: lesser(range.high, intersectRange.high),
            });
        }
      });
    });

    return new RangeSet<T>(newRanges);
  }

  /**
   * Returns all ranges in this set in ascending order. Returned ranges never
   * touch.
   */
  public ranges(): Array<ClosedOpenRange<T>> {
    return this.entries.slice(0);
  }
}

type RangeComparison = 'BELOW' | 'ABOVE' | 'TOUCHING';

/**
 * Determines if the range1 is below, above, or touching range2. Ranges "touch"
 * if they overlap or the lower range's top point is the same as the upper
 * range's bottom point.
 */
function compareRange<T>(range1: ClosedOpenRange<T>, range2: ClosedOpenRange<T>): RangeComparison {
  if (checkIsRangeLocalDateTime(range1, range2)) {
    const modifiedRange1 = range1 as ClosedOpenRange<LocalDateTime>;
    const modifiedRange2 = range2 as ClosedOpenRange<LocalDateTime>;
    if (modifiedRange1.high.isBefore(modifiedRange2.low)) {
      return 'BELOW';
    } else if (modifiedRange2.high.isBefore(modifiedRange1.low)) {
      return 'ABOVE';
    } else {
      return 'TOUCHING';
    }
  } else if (checkIsRangeLocalTime(range1, range2)) {
    const modifiedRange1 = range1 as ClosedOpenRange<LocalTime>;
    const modifiedRange2 = range2 as ClosedOpenRange<LocalTime>;
    if (modifiedRange1.high.isBefore(modifiedRange2.low)) {
      return 'BELOW';
    } else if (modifiedRange2.high.isBefore(modifiedRange1.low)) {
      return 'ABOVE';
    } else {
      return 'TOUCHING';
    }
  } else {
    if (range1.high < range2.low) {
      return 'BELOW';
    } else if (range2.high < range1.low) {
      return 'ABOVE';
    } else {
      return 'TOUCHING';
    }
  }
}

/**
 * Returns the smaller of the two inputs, or the second argument if they are
 * equal.
 */
function lesser<T>(x: T, y: T): T {
  if (checkIsLocalDateTime(x, y)) {
    const modifiedX = x as LocalDateTime;
    const modifiedY = y as LocalDateTime;
    return modifiedX.isBefore(modifiedY) ? x : y;
  } else if (checkIsLocalTime(x, y)) {
    const modifiedX = x as LocalTime;
    const modifiedY = y as LocalTime;
    return modifiedX.isBefore(modifiedY) ? x : y;
  } else {
    return x < y ? x : y;
  }
}
/**
 * Returns the greater of the two inputs, or the first argument if they are
 * equal.
 */
function greater<T>(x: T, y: T): T {
  if (checkIsLocalDateTime(x, y)) {
    const modifiedX = x as LocalDateTime;
    const modifiedY = y as LocalDateTime;
    return modifiedX.isBefore(modifiedY) ? y : x;
  } else if (checkIsLocalTime(x, y)) {
    const modifiedX = x as LocalTime;
    const modifiedY = y as LocalTime;
    return modifiedX.isBefore(modifiedY) ? y : x;
  } else {
    return x < y ? y : x;
  }
}

function checkIsLocalDateTime<T>(x: T, y: T): boolean {
  return x instanceof LocalDateTime && y instanceof LocalDateTime;
}

function checkIsLocalTime<T>(x: T, y: T): boolean {
  return x instanceof LocalTime && y instanceof LocalTime;
}

function checkIsRangeLocalDateTime<T>(
  range1: ClosedOpenRange<T>,
  range2: ClosedOpenRange<T>
): boolean {
  return range1.high instanceof LocalDateTime && range2.high instanceof LocalDateTime;
}

function checkIsRangeLocalTime<T>(range1: ClosedOpenRange<T>, range2: ClosedOpenRange<T>): boolean {
  return range1.high instanceof LocalTime && range2.high instanceof LocalTime;
}
